// One lead (the sales pipeline's overview page): a hero with the café's name, where it is, its stage and priority, the figures that
// matter for a sales call, and the actions — message on WhatsApp first. Below it the facts that are known (what is not known is
// said once, not drawn as empty rows), the timeline with a quick note, and beside them the next follow-up, the owner,
// do-not-contact and, once the lead has become an organisation, the way to it.

import type { Lead, LeadMessageChannel } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Badge, Button, FactGrid, HeroStat, Menu, missingFacts, PageHero, PageSkeleton, useToast, type FactItem } from '@clavian/ui-web';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';

import { PROBLEM, problemCode } from '../../api/problems';
import { useProblem } from '../../api/use-problem';
import { useNow } from '../../floor/live';
import { LoadFailure } from '../../shell/LoadFailure';
import { Icon } from '../../shell/nav-icons';
import { Section } from '../../ui';
import { NotPermitted, RequirePlatformPermission, usePlatformAccessHere } from '../access';
import { HQ_PATHS } from '../paths';
import { HQ_LEADS, HQ_ORGANIZATIONS } from '../sections';
import { ChangeStageDialog, ConvertLeadDialog, DeleteLeadDialog, DoNotContactDialog, LogActivityDialog } from './LeadDialogs';
import { LeadFormDialog } from './LeadFormDialog';
import { ConvertedCard, DoNotContactCard, FollowUpCard, OwnerCard } from './LeadSideCards';
import { LeadTimeline } from './LeadTimeline';
import { LeadPriorityPill, LeadStagePill, ratingText } from './labels';
import { MessageDialog } from './MessageDialog';
import { useChangeLeadStage, useLead } from './queries';
import { DAY_MS } from './when';
import '../hq.css';

export function LeadPage(): ReactNode {
  return (
    <RequirePlatformPermission permission={HQ_LEADS}>
      <LeadView />
    </RequirePlatformPermission>
  );
}

function LeadView(): ReactNode {
  const { t } = useTranslation('hq');
  const params = useParams({ strict: false }) as { leadId?: string };
  const lead = useLead(params.leadId ?? '');

  if (lead.data === undefined) {
    if (lead.isError && problemCode(lead.error) === PROBLEM.forbidden) return <NotPermitted />;
    const code = problemCode(lead.error);
    if (lead.isError && (code === PROBLEM.notFound || code === PROBLEM.validationFailed)) {
      return (
        <div className="console-page">
          <Link className="console-hq-back" to={HQ_PATHS.pipeline}>
            {t('leads.lead.back')}
          </Link>
          <h1 className="console-page__title">{t('leads.lead.notFoundTitle')}</h1>
          <p>{t('leads.lead.notFound')}</p>
        </div>
      );
    }
    return lead.isError ? (
      <div className="console-page">
        <LoadFailure error={lead.error} message={t('leads.lead.unavailable')} onRetry={() => void lead.refetch()} />
      </div>
    ) : (
      <PageSkeleton label={t('leads.lead.loading')} />
    );
  }
  return <LeadDetail lead={lead.data} />;
}

type Dialogs = {
  readonly message?: LeadMessageChannel;
  readonly log?: true;
  readonly stage?: true;
  readonly edit?: true;
  readonly convert?: true;
  readonly remove?: true;
  readonly dnc?: 'set' | 'clear';
};

function LeadDetail({ lead }: { readonly lead: Lead }): ReactNode {
  const { t } = useTranslation('hq');
  const format = useVenueFormatter();
  const navigate = useNavigate();
  const problems = useProblem();
  const { show } = useToast();
  const access = usePlatformAccessHere();
  const change = useChangeLeadStage();
  const now = useNow(60_000);
  const [open, setOpen] = useState<Dialogs>({});
  const [copied, setCopied] = useState(false);
  const close = () => setOpen({});

  const place = [lead.city, lead.area].filter((part): part is string => part !== null && part !== '').join(' · ');
  const canConvert = access.permissions.has(HQ_ORGANIZATIONS) && lead.organizationId === null;
  const whatsAppBlocked = lead.doNotContact ? t('leads.lead.whatsAppDnc') : !lead.phoneMobile ? t('leads.lead.whatsAppNoMobile') : undefined;
  const daysSince = lead.lastContactedAt === null ? undefined : Math.max(0, Math.floor((now - new Date(lead.lastContactedAt).getTime()) / DAY_MS));

  const copyPhone = () => {
    if (lead.phone === null) return;
    void navigator.clipboard?.writeText(lead.phone).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      },
      () => undefined,
    );
  };

  const whatsApp = (
    <Button variant="primary" icon={<Icon name="message" />} disabled={whatsAppBlocked !== undefined} {...(whatsAppBlocked === undefined ? {} : { disabledReason: whatsAppBlocked })} onClick={() => setOpen({ message: 'whatsapp' })}>
      {t('leads.lead.messageWhatsApp')}
    </Button>
  );

  const menu = (
    <Menu
      items={[
        ...(lead.instagram === null ? [] : [{ id: 'instagram', label: t('leads.lead.messageInstagram'), onSelect: () => setOpen({ message: 'instagram' }) }]),
        { id: 'log', label: t('leads.lead.logActivity'), onSelect: () => setOpen({ log: true }) },
        { id: 'stage', label: t('leads.lead.changeStage'), onSelect: () => setOpen({ stage: true }) },
        { id: 'edit', label: t('leads.lead.edit'), onSelect: () => setOpen({ edit: true }) },
        ...(canConvert ? [{ id: 'convert', label: t('leads.lead.convert'), onSelect: () => setOpen({ convert: true }) }] : []),
        { kind: 'separator' as const, id: 'sep' },
        { id: 'delete', label: t('leads.lead.delete'), tone: 'danger' as const, onSelect: () => setOpen({ remove: true }) },
      ]}
      trigger={<Button variant="secondary" icon={<Icon name="more" />} label={t('leads.lead.moreActions')} />}
    />
  );

  const items: readonly FactItem[] = [
    { key: 'contact', label: t('leads.lead.facts.contact'), value: lead.contactName },
    {
      key: 'phone',
      label: t('leads.lead.facts.phone'),
      value:
        lead.phone === null ? null : (
          <span className="console-lead-phone">
            <span className="console-num">{lead.phone}</span>
            <Button size="sm" variant="ghost" title={t('leads.lead.facts.copyPhone')} onClick={copyPhone}>
              {copied ? t('leads.lead.facts.copied') : t('leads.lead.facts.copy')}
            </Button>
            {lead.phoneMobile ? null : <span className="console-hq-secondary">{t('leads.lead.facts.landline')}</span>}
          </span>
        ),
    },
    { key: 'email', label: t('leads.lead.facts.email'), value: lead.email === null ? null : <a href={`mailto:${lead.email}`}>{lead.email}</a> },
    {
      key: 'instagram',
      label: t('leads.lead.facts.instagram'),
      value:
        lead.instagram === null ? null : (
          <a href={`https://instagram.com/${lead.instagram}`} target="_blank" rel="noopener noreferrer">
            @{lead.instagram}
          </a>
        ),
    },
    { key: 'address', label: t('leads.lead.facts.address'), value: lead.addressHint },
    {
      key: 'map',
      label: t('leads.lead.facts.map'),
      value:
        lead.mapUrl === null ? null : (
          <a href={lead.mapUrl} target="_blank" rel="noopener noreferrer">
            {t('leads.lead.facts.openMap')}
          </a>
        ),
    },
    { key: 'platforms', label: t('leads.lead.facts.platforms'), value: lead.platforms.length === 0 ? null : lead.platforms.join(', ') },
    { key: 'software', label: t('leads.lead.facts.software'), value: lead.currentSoftware },
    { key: 'hook', label: t('leads.lead.facts.hook'), value: lead.hook },
    { key: 'source', label: t('leads.lead.facts.source'), value: t(`leads.source.${lead.source}`) },
    { key: 'created', label: t('leads.lead.facts.created'), value: format.date(lead.createdAt, 'medium') },
  ];
  const absent = missingFacts(items).map((item) => String(item.label));

  return (
    <div className="console-page console-page--wide">
      <PageHero
        breadcrumb={
          <Link className="console-hq-back" to={HQ_PATHS.pipeline}>
            {t('leads.lead.back')}
          </Link>
        }
        title={lead.businessName}
        tileName={lead.businessName}
        meta={[place, lead.stage === 'lost' && lead.lostReason !== null ? t('leads.lead.lost', { reason: t(`leads.lostReason.${lead.lostReason}`) }) : ''].filter((part) => part !== '').join(' · ') || undefined}
        status={
          <>
            <LeadStagePill stage={lead.stage} />
            <LeadPriorityPill priority={lead.priority} />
            {lead.doNotContact ? (
              <Badge tone="danger" dot>
                {t('leads.lead.dnc.badge')}
              </Badge>
            ) : null}
          </>
        }
        actions={
          <>
            {lead.stage === 'contacted' ? (
              <Button
                variant="secondary"
                busy={change.isPending}
                onClick={() =>
                  change.mutate(
                    { leadId: lead.id, body: { stage: 'replied' } },
                    {
                      onSuccess: () => show({ tone: 'ok', title: t('leads.lead.markedReplied', { name: lead.businessName }) }),
                      onError: (error) => show({ tone: 'danger', title: problems.text(error, { fallback: t('leads.pipeline.moveFailed') }) }),
                    },
                  )
                }
              >
                {t('leads.lead.markReplied')}
              </Button>
            ) : null}
            {whatsApp}
            {menu}
          </>
        }
        stats={
          <>
            <HeroStat label={t('leads.lead.stats.rating')} value={lead.googleRatingTenths === null ? '—' : ratingText(lead.googleRatingTenths)} of={lead.googleRatingTenths === null ? undefined : t('leads.lead.stats.ofFive')} />
            <HeroStat label={t('leads.lead.stats.reviews')} value={lead.googleReviewCount ?? '—'} />
            <HeroStat label={t('leads.lead.stats.stations')} value={lead.stationCount ?? '—'} />
            <HeroStat label={t('leads.lead.stats.sinceContact')} value={daysSince ?? t('leads.lead.stats.noContact')} />
          </>
        }
      />

      <div className="console-lead-layout">
        <div className="console-lead-layout__main">
          <Section>
            <FactGrid
              items={items}
              missing={t('leads.lead.facts.missing', { names: absent.join(', ') })}
              missingAction={
                <Button size="sm" variant="ghost" onClick={() => setOpen({ edit: true })}>
                  {t('leads.lead.facts.missingAction')}
                </Button>
              }
            />
          </Section>
          <LeadTimeline leadId={lead.id} />
        </div>
        <aside className="console-lead-layout__side">
          <ConvertedCard lead={lead} />
          <FollowUpCard lead={lead} />
          <OwnerCard lead={lead} />
          <DoNotContactCard lead={lead} onToggle={(mode) => setOpen({ dnc: mode })} />
        </aside>
      </div>

      <MessageDialog leadId={open.message === undefined ? undefined : lead.id} initialChannel={open.message} onClose={close} />
      <LogActivityDialog lead={open.log === true ? lead : undefined} onClose={close} />
      <ChangeStageDialog lead={open.stage === true ? lead : undefined} onClose={close} />
      <LeadFormDialog open={open.edit === true} onOpenChange={(next) => (next ? undefined : close())} lead={lead} />
      <ConvertLeadDialog lead={open.convert === true ? lead : undefined} onClose={close} />
      <DoNotContactDialog lead={lead} mode={open.dnc} onClose={close} />
      <DeleteLeadDialog
        lead={open.remove === true ? lead : undefined}
        onClose={close}
        onDeleted={() => {
          close();
          void navigate({ to: HQ_PATHS.pipeline });
        }}
      />
    </div>
  );
}
